import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * Help & Reference page. Two top-level parts — "Using the app" and "The
 * physics model" — laid out as a sticky table-of-contents rail beside a
 * scrolling column of cards. The TOC tracks the section in view via an
 * IntersectionObserver scrollspy and jumps on click.
 *
 * The physics descriptions are intentionally conceptual but source-accurate:
 * see damperlib/physics/ (spec_slices, forces, rhs, solve, p2h_validate) and
 * docs/architecture.md for the implementation behind each claim. Where the
 * model leans on measured reference data rather than first principles, the
 * "First principles vs reference data" section says so plainly.
 */

type TocItem = { id: string; label: string };
type TocGroup = { group: string; items: readonly TocItem[] };

const TOC: readonly TocGroup[] = [
  {
    group: "Using the app",
    items: [
      { id: "help-overview", label: "Overview" },
      { id: "help-studies", label: "Studies & workspace" },
      { id: "help-create", label: "Creating a study" },
      { id: "help-runs", label: "Runs, KPIs & AutoResearch" },
    ],
  },
  {
    group: "The physics model",
    items: [
      { id: "help-rig", label: "The Virtual 7-Post Rig" },
      { id: "help-pipeline", label: "From setup to model" },
      { id: "help-submodels", label: "The sub-models" },
      { id: "help-assembly", label: "Equations of motion" },
      { id: "help-actuation", label: "Rig actuation & replay" },
      { id: "help-scoring", label: "Validation & scoring" },
      { id: "help-philosophy", label: "First principles vs reference" },
    ],
  },
];

const ALL_IDS: readonly string[] = TOC.flatMap((g) => g.items.map((i) => i.id));

function HelpSection({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="card help-section">
      <div className="head">
        <span className="title">{title}</span>
      </div>
      <div className="body help-prose">{children}</div>
    </section>
  );
}

/** Inline monospace token for identifiers / channel names / code. */
function Mono({ children }: { children: ReactNode }) {
  return <code className="mono">{children}</code>;
}

export function HelpView() {
  const [activeId, setActiveId] = useState<string>(ALL_IDS[0]);
  // Full visibility map so the active section is the topmost one currently in
  // the scrollspy band, regardless of scroll direction. IntersectionObserver
  // callbacks only carry the entries that changed, so we accumulate here.
  const visible = useRef<Map<string, boolean>>(new Map());

  useEffect(() => {
    const els = ALL_IDS.map((id) => document.getElementById(id)).filter(
      (el): el is HTMLElement => el !== null,
    );
    if (els.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          visible.current.set(entry.target.id, entry.isIntersecting);
        }
        const topmost = ALL_IDS.find((id) => visible.current.get(id));
        if (topmost) setActiveId(topmost);
      },
      // Band sits just below the sticky page header down to the upper third of
      // the viewport, so a section activates as its heading reaches the top.
      { rootMargin: "-72px 0px -68% 0px", threshold: 0 },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const jumpTo = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveId(id);
  };

  return (
    <div className="page-body help-page">
      <div className="page-head">
        <div className="page-title">
          <h1>Help &amp; Reference</h1>
          <div className="page-sub">
            How to drive the Virtual 7-Post Rig workflow, and how the back-end
            physics models fit together.
          </div>
        </div>
      </div>

      <div className="help-grid">
        <nav className="help-toc" aria-label="Help contents">
          {TOC.map((group) => (
            <div className="help-toc-group" key={group.group}>
              <div className="help-toc-group-label">{group.group}</div>
              {group.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`help-toc-link${
                    activeId === item.id ? " active" : ""
                  }`}
                  aria-current={activeId === item.id ? "true" : undefined}
                  onClick={() => jumpTo(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          ))}
        </nav>

        <div className="help-content">
          {/* ----------------------------- Using the app ---------------------------- */}
          <HelpSection id="help-overview" title="Overview">
            <p>
              This app is the engineer-facing front end of the{" "}
              <strong>Virtual Seven-Post Rig</strong> — a physics model that
              predicts what a real 7-post rig would measure for any track and
              any car setup, without needing rig time. Everything you do here
              flows through that one model.
            </p>
            <p>The workflow, end to end:</p>
            <ol>
              <li>
                <strong>Pick a rig input and a car setup</strong> — a recorded
                track lap or 7-post run to drive the rig, plus the setup sheet
                (springs, dampers, ARBs, geometry).
              </li>
              <li>
                <strong>Run a reference prediction (R0)</strong> — the baseline
                the model produces for that input and setup.
              </li>
              <li>
                <strong>Try candidate changes</strong> — edit the dampers or
                setup and run again; each candidate is scored against the same
                rig input.
              </li>
              <li>
                <strong>Compare against KPIs</strong> — read the waveform
                overlays and KPI table to see what moved.
              </li>
              <li>
                <strong>Or let AutoResearch search</strong> — Claude proposes
                setup changes for you, each scored on the real rig physics.
              </li>
            </ol>
            <p>
              Three tabs run across the top: <strong>Studies</strong> (where the
              work happens), <strong>Settings</strong> (Anthropic key and
              model), and <strong>Help</strong> (this page).
            </p>
          </HelpSection>

          <HelpSection id="help-studies" title="Studies & the workspace">
            <p>
              A <strong>Study</strong> is the unit of work. It pins one rig
              input and one baseline setup, and holds every run you spawn from
              them. The left rail lists your studies, newest first; the{" "}
              <Mono>◂ / ≡</Mono> gutter button collapses it so the charts can
              dominate. Selecting a study opens its detail view;{" "}
              <strong>New</strong> opens the creation wizard.
            </p>
            <p>Every study has:</p>
            <ul>
              <li>
                a <strong>Reference run (R0)</strong> — the baseline prediction,
                computed once;
              </li>
              <li>
                any number of <strong>Candidate runs</strong> — variations you
                create from R0.
              </li>
            </ul>
            <p>
              Runs are <strong>immutable</strong>: changing a setting never
              edits a run in place, it creates a new one — so the full history
              of what you tried is preserved.
            </p>
          </HelpSection>

          <HelpSection id="help-create" title="Creating a study">
            <p>
              <strong>New → “New Virtual Rig Study”</strong> collects four
              inputs, one per card:
            </p>
            <ul>
              <li>
                <strong>Track input</strong> — the rig actuation. The recorded
                motion that drives the rig: either a <em>7-post recording</em>{" "}
                (the rig’s own road-displacement channels) or a{" "}
                <em>track-lap replay</em> (a recorded lap, converted into pad
                motion). A lab / rig-characterisation mode is also available for
                synthetic inputs.
              </li>
              <li>
                <strong>Car platform</strong> — the setup, read from the setup
                XLSM: springs, dampers, ARBs, ride heights, corner weights,
                geometry. Use the event’s own setup sheet or borrow a donor
                campaign’s.
              </li>
              <li>
                <strong>Tyre sidewall</strong> — the tyre model (its vertical
                stiffness), from the event tyre catalogue or a manual override.
              </li>
              <li>
                <strong>Damper config</strong> — the damper force-velocity
                curves and click settings.
              </li>
            </ul>
            <p>
              On create, the backend assembles the physics spec from those
              inputs and queues R0. When R0 finishes, its results and a full
              input-provenance report appear in the study.
            </p>
          </HelpSection>

          <HelpSection id="help-runs" title="Runs, KPIs & AutoResearch">
            <h4>Reading a run</h4>
            <p>
              R0 and each candidate produce per-corner{" "}
              <strong>damper-position</strong> waveforms,{" "}
              <strong>ride height</strong>, <strong>tyre load</strong>,{" "}
              <strong>KPI scores</strong>, and a <strong>physics-fit</strong>{" "}
              report. The Results pane overlays candidate waveforms on R0 so you
              can see exactly where a change bites; the KPI table quantifies it.
            </p>
            <h4>Objective & guardrails</h4>
            <p>
              The <strong>Objective</strong> is the KPI you’re trying to
              improve. <strong>Guardrails</strong> are validity limits a
              candidate must respect to count as a real result — the run must
              actually complete and stay physically feasible. The{" "}
              <strong>Physics fit</strong> and <strong>Input provenance</strong>{" "}
              sections show how trustworthy the underlying inputs are: a
              candidate built on fallback data is flagged, not silently trusted.
            </p>
            <h4>AutoResearch</h4>
            <p>
              Instead of editing by hand, you can let <strong>Claude</strong>{" "}
              drive. It proposes damper-setting (and force-velocity scale)
              changes as new candidate runs, each one{" "}
              <strong>scored on the real rig physics</strong> against your
              objective and filtered by your guardrails. It iterates, learns
              from what it has already tried, and reports the best feasible
              setup it found. Start it from the AutoResearch panel inside a
              study.
            </p>
          </HelpSection>

          {/* --------------------------- The physics model -------------------------- */}
          <HelpSection id="help-rig" title="The Virtual 7-Post Rig">
            <p>
              A real <strong>seven-post rig</strong> holds a car on four
              wheel-pad actuators (one under each tyre) and three “body posts”
              that push on the chassis to mimic aerodynamic load — seven
              actuators in total. It replays a track’s measured wheel motions
              and aero loads in the lab so engineers can read how the suspension
              responds. The Virtual Rig reproduces that measurement{" "}
              <strong>in software</strong>.
            </p>
            <p>
              The model is a <strong>7-degree-of-freedom</strong> vehicle: one{" "}
              <strong>sprung body</strong> (the chassis) free to{" "}
              <strong>heave, pitch and roll</strong>, plus{" "}
              <strong>four unsprung corner masses</strong>, each free to move
              vertically. Internally that is an 18-state system — the seven
              positions and their velocities, plus a short force-lag state for
              each damper. Given the rig’s actuator inputs and the car setup, it
              integrates the equations of motion forward in time and predicts
              what every channel on the real rig would have recorded: per-corner
              damper position, ride height, tyre load and body attitude.
            </p>
            <p className="help-aside">
              The rig has no yaw, longitudinal or lateral freedom — it is a
              vertical-dynamics machine, so the model constrains those out.
            </p>
          </HelpSection>

          <HelpSection id="help-pipeline" title="From setup to model">
            <p>Three stages turn a setup sheet into a runnable model:</p>
            <div className="help-flow" aria-hidden="true">
              <span className="node">Setup XLSM</span>
              <span className="arrow">→</span>
              <span className="node">BaseCarSpec</span>
              <span className="arrow">→</span>
              <span className="node">PhysicsSpec</span>
              <span className="arrow">→</span>
              <span className="node">Solver</span>
              <span className="arrow">→</span>
              <span className="node">Prediction</span>
            </div>
            <ul>
              <li>
                <strong>Setup XLSM → BaseCarSpec.</strong> The JHR setup
                workbook is parsed into a typed car description — spring rates,
                motion ratios, ARB geometry, corner weights, tyre rates — with{" "}
                <strong>per-field provenance</strong> attached to every value
                (where it came from, or that it fell back to a default). All
                conversion to SI units happens here.
              </li>
              <li>
                <strong>BaseCarSpec → PhysicsSpec.</strong> The description is
                split into narrow <strong>spec slices</strong>, one per
                subsystem: body, dampers, tyres, springs, ARBs, kinematics,
                third elements, bump rubbers. Each slice isolates one piece of
                physics so it can evolve independently.
              </li>
              <li>
                <strong>PhysicsSpec → Solver.</strong> The slices feed the force
                assembly and the integrator (see “Equations of motion”).
              </li>
            </ul>
            <p>
              Because every field carries provenance, the model can tell you
              exactly which numbers are trusted setup data and which are
              fallbacks — that is what the Input-provenance report surfaces.
            </p>
          </HelpSection>

          <HelpSection id="help-submodels" title="The sub-models">
            <p>
              Each subsystem contributes a force. The principle the model uses
              for each:
            </p>
            <ul>
              <li>
                <strong>Springs</strong> — linear coil force, with the
                wheel-rate stiffness derived from first principles as{" "}
                <Mono>k_wheel = k_spring × MR²</Mono> (spring rate times motion
                ratio squared), not looked up.
              </li>
              <li>
                <strong>Anti-roll bars</strong> — the <em>bar’s</em> torsional
                stiffness is computed analytically from its geometry (
                <Mono>G·J/L</Mono> from diameter, wall thickness, length and
                material). The <em>blade</em> compliance, which dominates the
                assembled bar, comes from a measured stiffness-vs-angle table
                and is combined in series with the bar.
              </li>
              <li>
                <strong>Dampers</strong> — force-velocity behaviour comes from{" "}
                <strong>measured dyno curves</strong> (the PVP library),
                interpolated smoothly and given a short first-order velocity lag
                plus gas pre-charge. <strong>Clicks</strong> select which
                measured curve applies (they change the curve’s <em>shape</em>);
                the experimental <strong>fv-scale</strong> lever scales the
                whole curve’s <em>magnitude</em>.
              </li>
              <li>
                <strong>Tyres</strong> — modelled as a{" "}
                <strong>vertical contact-patch spring plus damping</strong>{" "}
                (force = stiffness × deflection, with no tensile force when the
                tyre lifts off). Stiffness comes from the setup sheet or the
                Firestone tyre catalogue. Lateral and longitudinal tyre
                behaviour is not modelled — this is a vertical rig.
              </li>
              <li>
                <strong>Third elements & bump rubbers</strong> — both use{" "}
                <strong>gap-then-Hooke</strong>: no force until the gap closes,
                then a rising force. Third elements (heave springs) add a linear
                rate past the gap; bump rubbers follow a measured
                force-deflection curve. Engagement is decided by comparing
                compression to the gap, not curve-fit.
              </li>
              <li>
                <strong>Body / chassis</strong> — treated as a{" "}
                <strong>single rigid sprung mass</strong> with full non-linear
                rotational dynamics (no small-angle approximation). There is no
                chassis-torsion degree of freedom — rig data can’t isolate one,
                so it isn’t invented.
              </li>
              <li>
                <strong>Kinematics</strong> — a constant{" "}
                <strong>motion ratio</strong> per corner maps wheel travel to
                damper travel; suspension forces act at the wheel/axle station
                derived from the setup’s wheelbase and track.
              </li>
            </ul>
          </HelpSection>

          <HelpSection id="help-assembly" title="Equations of motion">
            <p>
              One routine, <Mono>assemble_forces</Mono>, is the single place
              every subsystem force is summed for a given state — spring,
              damper, tyre, ARB, third-element, bump-rubber and body-post
              contributions, resolved into a total heave force, pitch and roll
              moments on the body, and a net vertical force at each wheel. The
              right-hand-side function then divides those by the masses and
              moments of inertia (adding gravity) to get accelerations.
            </p>
            <p>Two solves share that same force model:</p>
            <ul>
              <li>
                <strong>Static equilibrium</strong> sets the initial condition.
                It root-finds the pose where all seven accelerations are zero —
                including the dampers’ static force and the recording’s mean
                aero load — so the simulation starts at the car’s real resting
                posture. For setups with gap-springs it first solves which gaps
                are engaged.
              </li>
              <li>
                <strong>Dynamic integration</strong> then marches forward with a
                stiff implicit integrator (SciPy’s BDF), sampling the predicted
                channels onto a uniform time grid.
              </li>
            </ul>
            <p>
              Crucially, diagnostics and validation reuse these{" "}
              <strong>exact</strong> production routines — there is no separate
              “simplified” model — so what you read is what the rig path
              actually computed.
            </p>
          </HelpSection>

          <HelpSection id="help-actuation" title="Rig actuation & replay">
            <p>
              The model is driven by the same channels a real rig uses:{" "}
              <strong>four wheel-pad displacements</strong> (one per corner) and
              up to <strong>three body-post forces</strong> (the aero proxy).
              Both are interpolated smoothly from the recording.
            </p>
            <p>There are two ways to source them:</p>
            <ul>
              <li>
                <strong>7-post replay</strong> — the pad displacements come
                straight from a recorded rig run’s road-displacement channels.
              </li>
              <li>
                <strong>Track replay</strong> — from a recorded track lap, the
                pad displacement is back-calculated from the measured
                contact-patch load: <Mono>z_pad ≈ load ÷ tyre stiffness</Mono>.
                The same tyre stiffness is used for the back-calculation and the
                forward model, so the two stay consistent.
              </li>
            </ul>
            <p>
              One consequence worth knowing: under track replay the pad motion
              is prescribed, so the{" "}
              <strong>damper has only second-order authority over tyre load</strong>{" "}
              — changing damping mostly moves the body and damper-position
              channels, not the contact-patch load itself.
            </p>
          </HelpSection>

          <HelpSection id="help-scoring" title="Validation & scoring">
            <p>
              To check the model, its predicted per-corner{" "}
              <strong>damper position</strong> is reconstructed from the
              simulated motion and compared channel-by-channel against the rig’s
              recording. Two numbers gate correctness:
            </p>
            <ul>
              <li>
                <strong>
                  R²<sub>dyn</sub> &gt; 0.7
                </strong>{" "}
                per corner — a mean-subtracted R² that measures{" "}
                <em>dynamic</em> response accuracy, independent of any constant
                offset.
              </li>
              <li>
                <strong>|Δmean| &lt; 5 mm</strong> per corner — the average
                offset between predicted and recorded damper position (relaxed
                for setups with engaged gap-springs, which are under active
                rework).
              </li>
            </ul>
            <p>
              The forcing function is a <strong>corpus-wide gate</strong> that
              runs the simulator against every cached event with a parsable
              setup sheet and tabulates where each corner passes or fails. A
              change is only “correct” once it holds across the whole corpus —
              not just the event it was debugged on.
            </p>
          </HelpSection>

          <HelpSection
            id="help-philosophy"
            title="First principles vs reference data"
          >
            <p>
              The model’s guiding rule:{" "}
              <strong>
                derive physics from first principles wherever possible; use
                reference data as input and validation, never as the model
                itself.
              </strong>{" "}
              Spring wheel-rates come from rate × motion-ratio², ARB bars from{" "}
              <Mono>G·J/L</Mono>, gap-spring engagement from compression vs gap.
              Reference workbooks supply <em>parameters</em> (a measured
              blade-stiffness table, a bump-rubber curve, tyre-catalogue
              stiffnesses) and <em>validation targets</em> — they don’t define
              the model’s structure.
            </p>
            <p>Being honest about where measured data still drives the model:</p>
            <ul>
              <li>
                the ARB <strong>blade</strong> compliance is a measured table
                (the bar alone is analytical);
              </li>
              <li>
                <strong>bump-rubber</strong> force comes from a measured
                deflection curve (only the gap is analytical);
              </li>
              <li>
                <strong>tyre</strong> vertical stiffness is largely
                catalogue-driven, because the setup sheet’s tyre cell is
                unreliable for ovals;
              </li>
              <li>
                <strong>damper</strong> curves are measured dyno data when
                available, with a generic fallback otherwise.
              </li>
            </ul>
            <div className="help-note">
              <strong>Status.</strong> The pass criteria above are{" "}
              <strong>not yet met across the full corpus</strong> — the model is
              under active development, and the corpus gate is the live tally of
              where it stands. Read predictions with that in mind, especially on
              tracks and setups outside the cached, validated set.
            </div>
          </HelpSection>
        </div>
      </div>
    </div>
  );
}
